import React, { useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import CCEvents, { EventNames } from '@companycam/companycam-events';
import styled from 'styled-components';
import { get } from 'lodash';

import { defaultActiveFilter } from 'components/projects/taskList/constants';
import { graphQLIdDecoder } from 'helpers/graphql-utils';
import { setActiveFilterId } from 'actions/todos';
import useCreateTaskFromTemplate from '../hooks/useCreateTaskFromTemplates';

import TemplateBodyContent from './TemplateBodyContent';
import FooterButtons from './FooterButtons';
import TaskListHeader from 'components/projects/taskList/components/TaskListHeader';
import GET_TASK_LIST_TEMPLATE_HEADER_INFO from '../gql/getTaskListTemplateHeaderInfo.graphql.js';
import { Spinner } from '@companycam/slab-web';
import { translate } from 'translations/i18n';

// #region styled-components

const Body = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 0 var(--cc_size_spacing_l);
`;

const Footer = styled.div`
  position: sticky;
  bottom: -40px;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: var(--cc_size_spacing_m) var(--cc_size_spacing_l);
  background: var(--cc_color_background_1);
  border-block-start: var(--cc_size_border_width_s) solid
    var(--cc_color_border_default);
  &.btn-container {
    margin: 0;
    width: 100%;
  }
`;

// #endregion

const TemplateView = ({ projectNodeId, isEdit, onRequestClose }) => {
  const dispatch = useDispatch();
  const currentTemplateId = useSelector(
    (state) => state.todos.templates.currentTemplateId,
  );

  useEffect(() => {
    CCEvents.logEvent(EventNames.TASK_LIST_TEMPLATES.PREVIEW_TEMPLATE);
  }, []);

  const { data: templateHeaderData, loading: templateHeaderLoading } = useQuery(
    GET_TASK_LIST_TEMPLATE_HEADER_INFO,
    {
      variables: {
        id: currentTemplateId,
      },
    },
  );

  const templateInfo = get(templateHeaderData, 'viewer.taskListTemplate', {});

  const { createTaskListFromTemplate, loading: useLoading } =
    useCreateTaskFromTemplate({
      template: templateInfo,
      projectNodeId,
      onSuccess: (createdTaskListId) => {
        dispatch(setActiveFilterId(defaultActiveFilter));
        onRequestClose();
        if (createdTaskListId) {
          const { databaseId: projectId } = graphQLIdDecoder(projectNodeId);
          window.Turbo.visit(
            `/projects/${projectId}/todos/${createdTaskListId}`,
          );
        }
      },
    });

  if (templateHeaderLoading) {
    return <Spinner accessibilityLabel={translate('common.loading')} />;
  }

  return (
    <>
      <Body>
        <TaskListHeader template={templateInfo} projectId={projectNodeId} />
        <TemplateBodyContent projectNodeId={projectNodeId} />
      </Body>
      <Footer className="btn-container">
        <FooterButtons
          createTaskListFromTemplate={createTaskListFromTemplate}
          isEditing={isEdit}
          isLoading={useLoading}
          ownsTemplate={templateInfo.isOwner}
          onRequestClose={onRequestClose}
        />
      </Footer>
    </>
  );
};

TemplateView.propTypes = {
  isEdit: PropTypes.bool,
  projectNodeId: PropTypes.string,
  onRequestClose: PropTypes.func,
};

export default TemplateView;
